import { gql } from "@apollo/client";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateProgramMutation } from "~/graphql/__generated__/graphql";
import { useOrgConfig } from "~/providers/OrgConfigProvider";

type CreateProgramDialogProps = {
  onClose: () => void;
};

const CreateProgramDialog = ({
  onClose,
}: CreateProgramDialogProps) => {
  const { serviceLines } = useOrgConfig();
  const [name, setName] = useState("");
  const [serviceLineId, setServiceLineId] = useState<number | "">("");
  const navigate = useNavigate();

  const [createItem, { loading }] = useCreateProgramMutation({
    onCompleted: (data) => {
      navigate(`/predict-config/programs/${data.createProgram.id}`);
      onClose();
    },
  });

  const handleSubmit = useCallback(async () => {
    if (!name.trim() || !serviceLineId) return;

    await createItem({
      variables: {
        name: name.trim(),
        serviceLineId,
      },
      refetchQueries: ["OrgConfig"],
    });
  }, [name, createItem]);

  return (
    <Dialog open={true} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Create Program</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          type="text"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={loading}
        />
        <FormControl sx={{ width: "100%" }}>
          <InputLabel>Service Line</InputLabel>
          <Select
            margin="dense"
            label="Service Line"
            fullWidth
            value={serviceLineId}
            onChange={(e) => setServiceLineId(Number(e.target.value))}
            disabled={loading}
          >
            {Object.values(serviceLines).map(sl => sl && <MenuItem key={sl.id} value={sl.id}>{sl.name}</MenuItem>)}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading || !name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProgramDialog;

gql(`
  mutation CreateProgram(
    $name: String!
    $serviceLineId: Int!
  )
  @permission(requires: manageOrgConfig)
  {
    createProgram(
      name: $name
      serviceLineId: $serviceLineId
    ) {
      id
      name
      serviceLineId
    }
  }
`);
